import RenderComponentOrLoading from '@Components/RenderComponentOrLoading';
import { SENSOR_TYPE_LIST } from '@innovyze/shared-utils';
import {
	InfoCard,
	InputAutocomplete,
	InputText,
	SensorsAutocomplete,
	SensorType,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { STATUS_ENUM } from '@Types/editSensorData.types';
import { CreateUpdateSensorGroupFormValues } from '@Types/sensorGroup.types';
import { TSensorTypes } from '@Utils/valuemappingFormHelpers';
import { useFormikContext } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

const UpsertSensorGroupFields = () => {
	const { t } = useGlobalization();
	const multiTypeEnabled = useIsFeatureEnabled(
		'info-360-analytics-multi-type-sensor-group',
	);
	const { sensorGroupId } = useParams<{ sensorGroupId?: string }>();
	const isUpdate = !!sensorGroupId;
	const { values, errors, touched, setFieldTouched, setFieldValue } =
		useFormikContext<CreateUpdateSensorGroupFormValues>();
	const {
		initialized: sensorGroupByIdInitialized,
		paginatedSensorGroupPairs,
	} = useSelector(getValue).sensorGroup.sensorGroupById;

	const initialized = isUpdate
		? sensorGroupId &&
		  sensorGroupByIdInitialized &&
		  (paginatedSensorGroupPairs?.status === STATUS_ENUM.SUCCESS ||
				paginatedSensorGroupPairs?.status === STATUS_ENUM.ERROR)
		: true;

	const translationTypeValues = TSensorTypes(t);

	const sensorTypeOptions = SENSOR_TYPE_LIST.map(el => ({
		key: `${el}`,
		value: `${el}`,
		label: translationTypeValues[el] || el,
	}));

	if (multiTypeEnabled) {
		sensorTypeOptions.push({
			key: 'MultiType',
			value: 'MultiType',
			label: t('Multi-Type'),
		});
		sensorTypeOptions.sort((a, b) =>
			a.value > b.value ? 1 : b.value > a.value ? -1 : 0,
		);
	}

	return (
		<div style={{ maxWidth: '100%', width: 646 }}>
			<InfoCard fullHeight title={t('Sensor group')}>
				<RenderComponentOrLoading loading={!initialized}>
					<InputText
						dataCy="groupNameField"
						textField={{
							label: t('Group name'),
							error: touched.name && !!errors?.name,
							helperText: touched.name && errors?.name,
							variant: 'outlined',
							required: true,
						}}
						fieldAttrs={{ name: 'name' }}
					/>
					<InputAutocomplete
						dataCy="sensorTypeField"
						label={t('Sensor type')}
						options={sensorTypeOptions}
						fieldAttrs={{ name: 'sensorType' }}
						helperText={
							touched.sensorType ? errors?.sensorType : ''
						}
						error={touched.sensorType && !!errors?.sensorType}
						onChange={(_, selectedOption) => {
							setFieldTouched('sensorType', true);
							if (values.sensorType !== selectedOption?.value) {
								setFieldValue('sensors', []);
							}
						}}
						textFieldProps={{
							required: true,
							// closeMenuOnSelect: true,
						}}
						autocompleteProps={{
							disableCloseOnSelect: false,
						}}
					/>
					<SensorsAutocomplete
						dataCy="sensorsField"
						label={t('Sensors')}
						fieldAttrs={{ name: 'sensors' }}
						helperText={(touched.sensors && errors?.sensors) || ''}
						autocompleteProps={{
							disabled: !values.sensorType,
							noOptionsText: t('No Options'),
						}}
						error={touched.sensors && !!errors?.sensors}
						filters={
							values.sensorType !== 'MultiType'
								? {
										sensorType:
											values.sensorType as SensorType,
								  }
								: { excludeSourceType: [] }
						}
					/>
				</RenderComponentOrLoading>
			</InfoCard>
		</div>
	);
};

export default UpsertSensorGroupFields;
